import { FC, useState, MouseEvent } from 'react';
import Cookies from 'js-cookie';
import ConfirmationDialog from 'src/components/DialogBox/ConfirmationDialog/ConfirmationDialog';
import {
  actionButtons,
  apiUrls,
  confirmMessages,
  dataStorageKeywords,
  surchargeDialogTitles,
  surchargeDialogSubtitles,
} from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { capitalizeFirstLetter } from 'src/utils/text.utils';
import { Surcharge } from 'src/models/Management.interface';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import { surchargeInitialValues } from 'src/utils/initialValues.utils';
import { surchargeValidationSchema } from 'src/utils/validationScheme.utils';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox2';
import TextField from 'src/components/InputFields/TextField';
import TextArea from 'src/components/InputFields/TextArea';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  ActionMenuButton,
  ActionMenuPopover,
  ActionMenuItem,
  MenuItemBox,
  MenuItemText,
  MenuItemsWrapper,
  MenuIcon,
  MenuItemWithIcon,
} from 'src/components/StyledComponents/StyledActionMenu';

const SurchargeActionMenuButton: FC<{
  rowId: any;
  name: string;
  status: string;
  resetPageAndRefresh: () => void;
  fetchSurcharges: () => void;
  deleteSurcharge: (id: string) => void;
}> = ({
  rowId,
  name,
  resetPageAndRefresh,
  fetchSurcharges,
  deleteSurcharge,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedName, setSelectedName] = useState<string | null>(null);
  const [selectedSurcharge, setSelectedSurcharge] = useState<Surcharge | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleEditFormSubmit = async () => {
    if (!selectedSurcharge) {
      return;
    }

    try {
      const response = await axiosTokenInstance.put(apiUrls.EDIT_SURCHARGE, {
        id: selectedSurcharge.id,
        name: formik.values.name,
        description: formik.values.description,
        percentage:
          formik.values.percentage === '' ? null : formik.values.percentage,
        defaultValue:
          formik.values.defaultValue === '' ? null : formik.values.defaultValue,
      });
      setEditDialogOpen(false);
      fetchSurcharges();
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleEditDialogOpen = async (surcharge: Surcharge) => {
    setSelectedSurcharge(surcharge);

    try {
      const response = await axiosTokenInstance.get(
        apiUrls.GET_SURCHARGE_BY_ID,
        {
          params: { id: surcharge.id },
        }
      );

      formik.setValues({
        name: surcharge.name,
        description: response.data.description,
        percentage: response.data.percentage,
        defaultValue: response.data.defaultValue,
      });
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedSurcharge(null);
    formik.resetForm();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDialogOpen = (type: string) => {
    setDialogType(type);
    setSelectedName(name);
    setDialogOpen(true);
    handleClose();
  };

  const handleConfirm = async () => {
    if (!selectedName) {
      return;
    }
    const companyId = Cookies.get(dataStorageKeywords.COMPANY_ID);

    try {
      if (dialogType === actionButtons.DELETE) {
        if (rowId && companyId) {
          deleteSurcharge(rowId);
        }
        resetPageAndRefresh();
      }
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    } finally {
      setDialogOpen(false);
      setDialogType(null);
      setSelectedName(null);
    }
  };

  const formik = useFormik({
    initialValues: surchargeInitialValues,
    validationSchema: surchargeValidationSchema,
    onSubmit: handleEditFormSubmit,
  });

  return (
    <>
      <ActionMenuButton
        onClick={handleClick}
        component="i"
        className="bx bx-dots-horizontal-rounded"
      />
      <ActionMenuPopover open={open} anchorEl={anchorEl} onClose={handleClose}>
        <MenuItemsWrapper>
          <MenuItemWithIcon
            onClick={() =>
              handleEditDialogOpen({
                id: rowId,
                name: name,
                description: '',
                percentage: 0,
                defaultValue: 0,
              })
            }
            iconClassName="bx bx-edit"
            text="Edit"
          />

          <CustomDialog
            height="680px"
            open={editDialogOpen}
            handleClose={handleEditDialogClose}
            title={surchargeDialogTitles.EDIT}
            subtitle={surchargeDialogSubtitles.EDIT}
            onSubmit={formik.handleSubmit}
            smScreen={false}
            buttonLabel="Update"
          >
            {selectedSurcharge && (
              <>
                <TextField
                  width="480px"
                  id="name"
                  label="Surcharge Name"
                  formikProps={formik}
                />

                <TextArea
                  id="description"
                  label="Description"
                  formikProps={formik}
                />

                <TextField
                  width="480px"
                  id="percentage"
                  label="Percentage"
                  formikProps={formik}
                />
                <TextField
                  width="480px"
                  id="defaultValue"
                  label="Default Value"
                  formikProps={formik}
                />
              </>
            )}
          </CustomDialog>

          <MenuItemWithIcon
            onClick={() => handleDialogOpen(actionButtons.DELETE)}
            iconClassName="bx bx-trash"
            text="Delete"
          />
        </MenuItemsWrapper>
      </ActionMenuPopover>

      <ConfirmationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={handleConfirm}
        title={surchargeDialogTitles.DELETE}
        subtitle={confirmMessages.CONFIRM_DELETE_SURCHARGE}
        confirmText={capitalizeFirstLetter(dialogType || '')}
        confirmButtonColor={'var(--error)'}
        alertIconColor={'var(--error)'}
      />
    </>
  );
};
export default SurchargeActionMenuButton;
